// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — GrowthRoadmapPage
// Vertical skill tree with:
//   • TRUE gating (parent + consecutive months)
//   • Live metric sync from business data
//   • Tree View (SVG) ↔ List View toggle
// ══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Map, ArrowUpRight, RefreshCw, Flame, Lock, Clock, CheckCircle2,
  LayoutList, GitBranch
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { useAuth } from '@contexts/AuthContext';
import { supabase } from '@lib/supabase';
import { evaluateAllGates, canMarkComplete, summarizeGateStates } from '@services/milestoneGating';
import { syncMilestoneMetrics } from '@services/metricSyncEngine';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Grid, Badge, Button } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';
import { EmptyState } from '@components/ui/States';
import { MilestoneCard } from '@components/growth-roadmap/MilestoneCard';
import { SkillTreeSVG } from '@components/growth-roadmap/SkillTreeSVG';

export function GrowthRoadmapPage({ data, refresh }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();
  const { effectiveProfile } = useAuth();

  const [view, setView] = useState('tree');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const tenant = data?.tenant;
  const rawMilestones = data?.milestones || [];

  // ─── Evaluate gates for ALL milestones ──────────────────────────────
  const gatedMilestones = useMemo(
    () => evaluateAllGates(rawMilestones),
    [rawMilestones]
  );

  const summary = useMemo(
    () => summarizeGateStates(gatedMilestones),
    [gatedMilestones]
  );

  // ─── Auto-sync metrics on mount (once per session) ─────────────────
  useEffect(() => {
    if (!effectiveProfile?.tenant_id || !data?.milestones) return;
    let cancelled = false;
    (async () => {
      setSyncing(true);
      try {
        const result = await syncMilestoneMetrics({
          tenantId: effectiveProfile.tenant_id,
          data
        });
        if (cancelled) return;
        if (result.streakUpdates > 0 || result.synced > 0) {
          refresh?.(); // reload milestones with updated streaks
        }
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when tenant changes — not on every data refresh (prevents loop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProfile?.tenant_id]);

  // ─── Manual completion (only allowed for "ready" state) ─────────────
  const handleMarkComplete = useCallback(async (milestone) => {
    if (!canMarkComplete(milestone, gatedMilestones)) {
      return; // Button should be disabled in UI, but guard anyway
    }
    const { error } = await supabase
      .from('growth_milestones')
      .update({
        is_completed: true,
        manually_marked: true,
        completed_at: new Date().toISOString()
      })
      .eq('id', milestone.id);
    if (!error) refresh?.();
  }, [gatedMilestones, refresh]);

  // ─── Manual sync button ─────────────────────────────────────────────
  const handleManualSync = useCallback(async () => {
    if (!effectiveProfile?.tenant_id) return;
    setSyncing(true);
    setSyncMessage(null);
    const result = await syncMilestoneMetrics({
      tenantId: effectiveProfile.tenant_id,
      data
    });
    setSyncMessage({
      synced: result.synced,
      streakUpdates: result.streakUpdates,
      errors: result.errors.length
    });
    setSyncing(false);
    refresh?.();
    setTimeout(() => setSyncMessage(null), 4000);
  }, [effectiveProfile?.tenant_id, data, refresh]);

  if (rawMilestones.length === 0) {
    return (
      <div>
        <PageHeader
          icon={<Map size={22} color={theme.primary} />}
          title={t.growthRoadmapTitle}
          description={t.growthRoadmapDesc}
        />
        <EmptyState
          icon={<Map size={36} color={theme.primary} />}
          title={t.noDataYet}
          description={t.noDataDesc}
        />
      </div>
    );
  }

  // ─── Group milestones by level (for list view) ───────────────────────
  const byLevel = gatedMilestones.reduce((acc, m) => {
    if (!acc[m.level]) acc[m.level] = [];
    acc[m.level].push(m);
    return acc;
  }, {});
  const sortedLevels = Object.keys(byLevel).map(Number).sort((a, b) => b - a);

  const levelLabels = {
    5: t.levelUltimate, 4: t.levelMastery, 3: t.levelScale,
    2: t.levelGrowth, 1: t.levelFoundation
  };
  const levelVariants = {
    5: 'primary', 4: 'info', 3: 'warning', 2: 'success', 1: 'neutral'
  };

  // Goal progress
  const goalTarget = tenant?.goal_target || 1;
  const goalCurrent = tenant?.goal_current || 0;
  const goalProgress = (goalCurrent / goalTarget) * 100;
  const goalTitle = tenant?.goal_type === 'expand_branches'
    ? `${goalTarget} ${lang === 'ar' ? 'فرع' : 'branches'}`
    : tenant?.goal_type === 'increase_profit'
    ? `${t.sar} ${goalTarget.toLocaleString()} ${lang === 'ar' ? 'ربح' : 'profit'}`
    : goalTarget;

  return (
    <div>
      <PageHeader
        icon={<Map size={22} color={theme.primary} />}
        title={t.growthRoadmapTitle}
        description={t.growthRoadmapDesc}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualSync}
              disabled={syncing}
            >
              {syncing
                ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t.syncing}</>
                : <><RefreshCw size={13} /> {t.syncMetrics}</>
              }
            </Button>
          </div>
        }
      />

      {syncMessage && (
        <div style={{
          background: theme.successDim,
          border: `1px solid ${theme.primaryBorder}`,
          color: theme.success,
          padding: '10px 14px',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <CheckCircle2 size={14} />
          {lang === 'ar'
            ? `تم تحديث ${syncMessage.synced} مقياس`
            : `Synced ${syncMessage.synced} metrics`}
          {syncMessage.streakUpdates > 0 && (
            <span>
              · {lang === 'ar'
                ? `${syncMessage.streakUpdates} تحديث للسلسلة`
                : `${syncMessage.streakUpdates} streak updates`}
            </span>
          )}
        </div>
      )}

      {/* Summary KPIs */}
      <div style={{ marginBottom: 24 }}>
        <Grid min={180}>
          <StatCard
            icon={<CheckCircle2 size={20} color={theme.success} />}
            label={t.completedMilestones}
            value={summary.completed}
            accentColor={theme.success}
          />
          <StatCard
            icon={<Flame size={20} color={theme.primary} />}
            label={t.readyMilestones}
            value={summary.ready || 0}
          />
          <StatCard
            icon={<Clock size={20} color={theme.warning} />}
            label={t.inProgressMilestones}
            value={summary.in_progress || 0}
            accentColor={theme.warning}
          />
          <StatCard
            icon={<Lock size={20} color={theme.textTertiary} />}
            label={t.lockedMilestones}
            value={summary.locked || 0}
            accentColor={theme.textSecondary}
          />
        </Grid>
      </div>

      {/* Goal banner */}
      <Card style={{ padding: 22, marginBottom: 24 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div>
            <div style={{ fontSize: 11, color: theme.textTertiary }}>{t.goal}</div>
            <div style={{
              fontSize: 18,
              fontWeight: 700,
              color: theme.text,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <ArrowUpRight size={16} color={theme.primary} />
              {goalTitle}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: theme.textTertiary }}>{t.currentProgress}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: theme.primary }}>
              {goalCurrent} {t.goalOf} {goalTarget}
            </div>
          </div>
        </div>
        <div style={{
          height: 12,
          borderRadius: 6,
          background: theme.bgInput,
          overflow: 'hidden',
          marginBottom: 10
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(goalProgress, 100)}%`,
            background: `linear-gradient(90deg, ${theme.primary}, ${theme.info})`,
            borderRadius: 6,
            transition: 'width 1s ease-out'
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: theme.textSecondary
        }}>
          <span>
            {lang === 'ar'
              ? `أنجزت ${summary.completed} من ${gatedMilestones.length} مرحلة`
              : `${summary.completed} of ${gatedMilestones.length} milestones completed`}
          </span>
          <span>{((summary.completed / gatedMilestones.length) * 100).toFixed(0)}%</span>
        </div>
      </Card>

      {/* View toggle */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        paddingBottom: 4
      }}>
        {[
          { key: 'tree', label: t.treeView, icon: GitBranch },
          { key: 'list', label: t.listView, icon: LayoutList }
        ].map(v => {
          const Icon = v.icon;
          const active = view === v.key;
          return (
            <div
              key={v.key}
              onClick={() => setView(v.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: active ? theme.primaryDim : theme.bgInput,
                color: active ? theme.primary : theme.textSecondary,
                border: `1px solid ${active ? theme.primaryBorder : theme.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s'
              }}
            >
              <Icon size={12} />
              {v.label}
            </div>
          );
        })}
      </div>

      {/* Tree View */}
      {view === 'tree' && (
        <Card style={{ padding: 16, overflow: 'hidden' }}>
          <SkillTreeSVG
            milestones={gatedMilestones}
            onNodeClick={(m) => {
              if (m.gate?.state === 'ready') handleMarkComplete(m);
            }}
          />
        </Card>
      )}

      {/* List View */}
      {view === 'list' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24
        }}>
          {sortedLevels.map((lvl, lvlIdx) => (
            <div key={lvl}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 14
              }}>
                <Badge variant={levelVariants[lvl]}>Lv.{lvl}</Badge>
                <span style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
                  {levelLabels[lvl]}
                </span>
                <div style={{ flex: 1, height: 1, background: theme.border }} />
                <span style={{ fontSize: 11, color: theme.textTertiary }}>
                  {byLevel[lvl].filter(m => m.is_completed).length} / {byLevel[lvl].length}
                </span>
              </div>

              <Grid min={280}>
                {byLevel[lvl].map(milestone => (
                  <MilestoneCard
                    key={milestone.id}
                    milestone={milestone}
                    onMarkComplete={handleMarkComplete}
                  />
                ))}
              </Grid>

              {lvlIdx < sortedLevels.length - 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 20,
                  marginBottom: -4
                }}>
                  <div style={{
                    width: 2,
                    height: 24,
                    background: `linear-gradient(180deg, ${theme.primary}, ${theme.border})`
                  }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
